import { createPortal } from 'react-dom';
import { useHeaderActionsTarget } from '@/layouts/usePageTitle';
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Check, ChevronDown, CircleUserRound, EllipsisVertical, FileText, GitBranch, History, Image as ImageIcon, MessageSquare, Paperclip, Plus, Save, Search, Send, Trash2, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  addAgileComment,
  addAgileAttachment,
  addAgileRelation,
  createAgileTask,
  deleteAgileAttachment,
  deleteAgileRelation,
  deleteAgileTask,
  fetchAgileBoard,
  fetchAgileLookups,
  fetchAgileTaskDetail,
  searchAgileTasks,
  updateAgileTask,
  type AgileComment,
  type AgileLookups,
  type AgileStatus,
  type AgileTask,
  type AgileAttachment,
  type AgileTaskDetail,
  type AgileTaskPayload,
  type AgileRelatedTask,
} from '@/api/agile';
import { useI18n } from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import ImageCarouselModal from '@/pages/tasks/ImageCarouselModal';
import { confirmToast, useErrorToast, LoadRetry } from '@/lib/notify';

const BOARD_COLUMNS: AgileStatus[] = ['todo', 'in_progress', 'test', 'done', 'closed', 'canceled'];

const TASK_TYPE_COLORS: Record<string, { border: string; background: string; text: string; dot: string }> = {
  gelistirme: {
    border: '#2563eb',
    background: 'rgba(37, 99, 235, 0.12)',
    text: '#1d4ed8',
    dot: '#2563eb',
  },
  hata: {
    border: '#dc2626',
    background: 'rgba(220, 38, 38, 0.12)',
    text: '#991b1b',
    dot: '#dc2626',
  },
  iyilestirme: {
    border: '#16a34a',
    background: 'rgba(22, 163, 74, 0.12)',
    text: '#166534',
    dot: '#16a34a',
  },
  destek: {
    border: '#ea580c',
    background: 'rgba(234, 88, 12, 0.12)',
    text: '#c2410c',
    dot: '#ea580c',
  },
};

function normalizeTaskTypeKey(value: string | null | undefined): string {
  return (value ?? '')
    .toLocaleLowerCase('tr-TR')
    .replaceAll('ğ', 'g')
    .replaceAll('ü', 'u')
    .replaceAll('ş', 's')
    .replaceAll('ı', 'i')
    .replaceAll('ö', 'o')
    .replaceAll('ç', 'c')
    .replace(/[^a-z0-9]+/g, '');
}

function getTaskTypeColors(taskTypeCode: string | null | undefined, taskTypeName?: string | null | undefined) {
  const nameKey = normalizeTaskTypeKey(taskTypeName);
  const codeKey = normalizeTaskTypeKey(taskTypeCode);
  const key = nameKey || codeKey;

  if (key === 'gelitirme') return TASK_TYPE_COLORS.gelistirme;
  if (key === 'iyiletirme') return TASK_TYPE_COLORS.iyilestirme;

  return TASK_TYPE_COLORS[key] ?? null;
}

function priorityTone(priority: number): string {
  if (priority === 3) return 'bg-rose-100 text-rose-700';
  if (priority === 2) return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-700';
}

function statusTitle(status: AgileStatus, t: (key: string) => string): string {
  if (status === 'todo') return t('agile.status.todo');
  if (status === 'in_progress') return t('agile.status.inProgress');
  if (status === 'test') return t('agile.status.test');
  if (status === 'done') return t('agile.status.done');
  if (status === 'closed') return t('agile.status.closed');
  return t('agile.status.canceled');
}

function statusTone(status: AgileStatus): string {
  if (status === 'in_progress') return 'bg-blue-100 text-blue-700';
  if (status === 'test') return 'bg-amber-100 text-amber-700';
  if (status === 'done') return 'bg-emerald-100 text-emerald-700';
  if (status === 'closed') return 'bg-slate-200 text-slate-700';
  if (status === 'canceled') return 'bg-rose-100 text-rose-700';
  return 'bg-slate-100 text-slate-700';
}

function priorityLabel(priority: number, t: (key: string) => string): string {
  if (priority === 3) return t('agile.priority.high');
  if (priority === 2) return t('agile.priority.middle');
  return t('agile.priority.low');
}

function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('tr-TR');
}

function getDueDateInfo(value: string | null | undefined) {
  if (!value) return null;
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diffDays === 0) return { label: 'Bugün', className: 'text-amber-700 bg-amber-100' };
  if (diffDays > 0) return { label: `${diffDays} gün kaldı`, className: 'text-emerald-700 bg-emerald-100' };
  return { label: `${Math.abs(diffDays)} gün geçti`, className: 'text-rose-700 bg-rose-100' };
}

function isImageAttachment(fileUrl: string, mimeType: string | null): boolean {
  if (mimeType?.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp)$/i.test(fileUrl);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getMentionQuery(text: string, caretIndex: number): { start: number; query: string } | null {
  const beforeCaret = text.slice(0, caretIndex);
  const match = beforeCaret.match(/(?:^|\s)@([a-zA-Z0-9._-]*)$/);
  if (!match) return null;
  const query = match[1] ?? '';
  return {
    start: caretIndex - query.length - 1,
    query,
  };
}

function extractMentionedUserIds(text: string, users: AgileLookups['users']): number[] {
  const matches = text.matchAll(/(^|\s)@([a-zA-Z0-9._-]+)/g);
  const usernames = new Set<string>();

  for (const match of matches) {
    const username = match[2]?.trim().toLocaleLowerCase('en-US');
    if (username) usernames.add(username);
  }

  return users
    .filter((user) => usernames.has(user.username.toLocaleLowerCase('en-US')))
    .map((user) => user.id);
}

function renderCommentWithMentions(text: string) {
  const parts = text.split(/(@[a-zA-Z0-9._-]+)/g);
  return parts.map((part, index) => {
    if (/^@[a-zA-Z0-9._-]+$/.test(part)) {
      return (
        <span
          key={`${part}-${index}`}
          className="rounded bg-blue-50 px-1 py-0.5 font-medium text-blue-700"
        >
          {part}
        </span>
      );
    }
    return <span key={`text-${index}`}>{part}</span>;
  });
}


type BoardFilter = 'all' | 'mine';

function TaskTypeFilterDropdown({
  value,
  onChange,
  options,
  label,
  compact = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: AgileLookups['taskTypes'];
  label: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const selected = options.find((option) => String(option.id) === value) ?? null;
  const selectedColors = getTaskTypeColors(selected?.code, selected?.name);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex ${compact?'h-8 min-w-[150px] rounded-lg':'h-10 min-w-[190px] rounded-xl'} items-center justify-between gap-3 border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600`}
      >
        <span className="inline-flex items-center gap-2">
          {selected && selectedColors ? (
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: selectedColors.dot }} />
          ) : (
            <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
          )}
          <span>{selected?.name ?? label}</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
          <button
            type="button"
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
            className="flex w-full items-center justify-between px-3 py-2.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              {label}
            </span>
            {!value && <Check className="h-4 w-4 text-slate-500" />}
          </button>
          {options.map((option) => {
            const colors = getTaskTypeColors(option.code, option.name);
            const active = String(option.id) === value;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(String(option.id));
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between px-3 py-2.5 text-xs font-semibold transition hover:bg-slate-50"
                style={{ color: colors?.text ?? '#475569' }}
              >
                <span className="inline-flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: colors?.dot ?? '#94a3b8' }}
                  />
                  {option.name}
                </span>
                {active && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TaskCard({
  task,
  onOpen,
  onDragStart,
  onTouchDragStart,
  onTouchDragMove,
  onTouchDragEnd,
  shouldSuppressClick,
}: {
  task: AgileTask;
  onOpen: (task: AgileTask) => void;
  onDragStart: (task: AgileTask) => void;
  onTouchDragStart: (task: AgileTask) => void;
  onTouchDragMove: (clientX: number, clientY: number) => void;
  onTouchDragEnd: (clientX: number, clientY: number) => void;
  shouldSuppressClick: () => boolean;
}) {
  const { t } = useI18n();
  const taskTypeColors = getTaskTypeColors(task.task_type_code, task.task_type_name);
  const dueDateInfo = getDueDateInfo(task.due_date);
  const isCompactStatus = task.status === 'closed' || task.status === 'canceled';

  return (
    <button
      type="button"
      draggable
      onDragStart={() => onDragStart(task)}
      onClick={() => {
        if (shouldSuppressClick()) return;
        onOpen(task);
      }}
      onTouchStart={() => onTouchDragStart(task)}
      onTouchMove={(event) => {
        const touch = event.touches[0];
        if (!touch) return;
        event.preventDefault();
        onTouchDragMove(touch.clientX, touch.clientY);
      }}
      onTouchEnd={(event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        onTouchDragEnd(touch.clientX, touch.clientY);
      }}
      onTouchCancel={() => onTouchDragEnd(-1, -1)}
      className={`w-full rounded-lg border text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        isCompactStatus ? 'p-2' : 'p-2.5'
      }`}
      style={{
        borderColor: taskTypeColors?.border ?? '#e2e8f0',
        backgroundColor: taskTypeColors?.background ?? '#ffffff',
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          {!isCompactStatus && (
            <>
              <div className="text-xs font-medium uppercase tracking-[0.16em] text-blue-600">{task.module_name}</div>
              {task.task_type_name && (
                <div className="mt-1 text-[10px] font-medium" style={{ color: taskTypeColors?.text ?? '#7c3aed' }}>
                  {task.task_type_name}
                </div>
              )}
            </>
          )}
          <h3 className={`${isCompactStatus ? 'mt-0 line-clamp-2 text-[11px]' : 'mt-1 text-xs'} line-clamp-2 font-semibold leading-4.5 text-slate-800`}>
            {task.title}
          </h3>
        </div>
        <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${priorityTone(task.priority)}`}>
          {priorityLabel(task.priority, t)}
        </span>
      </div>

      {!isCompactStatus && (
        <>
          <div className="mt-2 text-[10px] text-slate-400">
            {t('agile.createdByLabel')}: {task.created_by_name || t('agile.unknownUser')}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
              Oluşturma: {formatShortDate(task.created_on)}
            </span>
            {task.due_date && dueDateInfo && (
              <span className={`rounded-full px-2 py-0.5 ${dueDateInfo.className}`}>
                Termin: {dueDateInfo.label}
              </span>
            )}
          </div>

          <div className="mt-2.5 flex items-center justify-between gap-2 text-[10px] text-slate-500">
            <div className="inline-flex items-center gap-1.5">
              <CircleUserRound className="h-2.5 w-2.5" />
              <span className="truncate">{task.assigned_user_name || t('agile.unassigned')}</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <MessageSquare className="h-2.5 w-2.5" />
              <span>{task.comment_count}</span>
            </div>
            {task.attachment_count > 0 && (
              <div className="inline-flex items-center gap-1.5">
                <Paperclip className="h-2.5 w-2.5" />
                <span>{task.attachment_count}</span>
              </div>
            )}
          </div>
        </>
      )}
    </button>
  );
}

function AgileTaskModal({
  task,
  lookups,
  onClose,
  onSaved,
  onRefreshBoard,
  onDuplicate,
  onOpenTask,
}: {
  task: AgileTask;
  lookups: AgileLookups;
  onClose: () => void;
  onSaved: () => void;
  onRefreshBoard: () => void;
  onDuplicate: (task: AgileTaskDetail | AgileTask, mode?: 'duplicate' | 'related') => void;
  onOpenTask: (task: AgileTask | AgileRelatedTask) => void;
}) {
  const { t } = useI18n();
  const currentUser = useAuthStore((s) => s.user);
  const [detail, setDetail] = useState<AgileTaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const [attachmentSaving, setAttachmentSaving] = useState(false);
  const [relationSaving, setRelationSaving] = useState(false);
  const [comment, setComment] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState<number | null>(null);
  const [relationModalOpen, setRelationModalOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [activeSideTab, setActiveSideTab] = useState<'history' | 'comments' | 'attachments' | 'relations'>('comments');
  const [relationQuery, setRelationQuery] = useState('');
  const [relationResults, setRelationResults] = useState<AgileRelatedTask[]>([]);
  const [relationSearchLoading, setRelationSearchLoading] = useState(false);
  const [form, setForm] = useState({
    title: task.title,
    description: task.description ?? '',
    moduleId: String(task.module_id),
    taskTypeId: task.task_type_id ? String(task.task_type_id) : '',
    priority: String(task.priority),
    assignedUserId: task.assigned_user_id ? String(task.assigned_user_id) : '',
    dueDate: task.due_date ?? '',
  });
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    fetchAgileTaskDetail(task.uuid_id)
      .then((res) => {
        setDetail(res);
        setForm({
          title: res.title,
          description: res.description ?? '',
          moduleId: String(res.module_id),
          taskTypeId: res.task_type_id ? String(res.task_type_id) : '',
          priority: String(res.priority),
          assignedUserId: res.assigned_user_id ? String(res.assigned_user_id) : '',
          dueDate: res.due_date ?? '',
        });
      })
      .finally(() => setLoading(false));
  }, [task.uuid_id]);

  useEffect(() => {
    if (!detail || relationQuery.trim().length < 2) {
      setRelationResults([]);
      setRelationSearchLoading(false);
      return undefined;
    }

    let active = true;
    const timeoutId = window.setTimeout(() => {
      setRelationSearchLoading(true);
      searchAgileTasks(relationQuery.trim(), detail.uuid_id)
        .then((results) => {
          if (!active) return;
          const existing = new Set(detail.related_tasks.map((item) => item.uuid_id));
          setRelationResults(
            results.filter(
              (item) =>
                !existing.has(item.uuid_id)
                && item.status !== 'closed'
                && item.status !== 'canceled',
            ),
          );
        })
        .catch(() => {
          if (active) setRelationResults([]);
        })
        .finally(() => {
          if (active) setRelationSearchLoading(false);
        });
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [detail, relationQuery]);

  useEffect(() => {
    if (!actionsOpen) return undefined;

    function handlePointerDown() {
      setActionsOpen(false);
    }

    document.addEventListener('click', handlePointerDown);
    return () => document.removeEventListener('click', handlePointerDown);
  }, [actionsOpen]);

  const imageAttachments = useMemo(
    () => (detail?.attachments ?? [])
      .filter((attachment) => isImageAttachment(attachment.file_url, attachment.mime_type))
      .map((attachment) => ({
        src: attachment.file_url,
        name: attachment.file_name,
      })),
    [detail],
  );

  const mentionableUsers = useMemo(
    () => lookups.users.filter((user) => user.is_active === 1),
    [lookups.users],
  );

  const filteredMentionUsers = useMemo(() => {
    const normalized = mentionQuery.trim().toLocaleLowerCase('tr-TR');
    if (!mentionOpen) return [];
    return mentionableUsers
      .filter((user) => {
        if (!normalized) return true;
        const username = user.username.toLocaleLowerCase('tr-TR');
        const fullName = (user.fullname ?? '').toLocaleLowerCase('tr-TR');
        return username.includes(normalized) || fullName.includes(normalized);
      })
      .slice(0, 8);
  }, [mentionOpen, mentionQuery, mentionableUsers]);

  useEffect(() => {
    setMentionSelectedIndex(0);
  }, [mentionQuery, mentionOpen]);

  async function handleSave() {
    setSaving(true);
    try {
      await updateAgileTask(task.uuid_id, {
        title: form.title.trim(),
        description: form.description.trim(),
        moduleId: Number(form.moduleId),
        taskTypeId: form.taskTypeId ? Number(form.taskTypeId) : null,
        priority: Number(form.priority),
        assignedUserId: form.assignedUserId ? Number(form.assignedUserId) : null,
        dueDate: form.dueDate || null,
      });
      toast.success(t('agile.updateSuccess'));
      onSaved();
    } catch {
      toast.error(t('agile.updateError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleAssignToMe() {
    if (!currentUser) return;
    setSaving(true);
    try {
      await updateAgileTask(task.uuid_id, { assignToMe: true });
      toast.success(t('agile.assignToMeSuccess'));
      onSaved();
    } catch {
      toast.error(t('agile.updateError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleAddComment() {
    if (!comment.trim()) return;
    setCommentSaving(true);
    try {
      const mentionUserIds = extractMentionedUserIds(comment.trim(), mentionableUsers);
      const created = await addAgileComment(task.uuid_id, comment.trim(), mentionUserIds);
      setDetail((prev) => prev ? {
        ...prev,
        comments: [created, ...prev.comments],
        comment_count: prev.comment_count + 1,
      } : prev);
      setComment('');
      setMentionOpen(false);
      setMentionQuery('');
      setMentionStart(null);
      toast.success(t('agile.commentAdded'));
      onRefreshBoard();
    } catch {
      toast.error(t('agile.commentError'));
    } finally {
      setCommentSaving(false);
    }
  }

  function handleCommentChange(value: string, caretIndex: number) {
    setComment(value);
    const mention = getMentionQuery(value, caretIndex);
    if (mention) {
      setMentionOpen(true);
      setMentionQuery(mention.query);
      setMentionStart(mention.start);
    } else {
      setMentionOpen(false);
      setMentionQuery('');
      setMentionStart(null);
    }
  }

  function applyMention(username: string) {
    if (mentionStart === null || !commentInputRef.current) return;
    const textarea = commentInputRef.current;
    const caret = textarea.selectionStart ?? comment.length;
    const nextValue = `${comment.slice(0, mentionStart)}@${username} ${comment.slice(caret)}`;
    setComment(nextValue);
    setMentionOpen(false);
    setMentionQuery('');
    setMentionStart(null);

    window.requestAnimationFrame(() => {
      const nextCaret = mentionStart + username.length + 2;
      textarea.focus();
      textarea.setSelectionRange(nextCaret, nextCaret);
    });
  }

  async function handleDelete() {
    const confirmed = await confirmToast(t('agile.deleteConfirm'), t('agile.deleteConfirmMessage'));
    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      await deleteAgileTask(task.uuid_id);
      toast.success(t('agile.deleteSuccess'));
      onSaved();
    } catch {
      toast.error(t('agile.deleteError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleAttachmentChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setAttachmentSaving(true);
    try {
      const created = await addAgileAttachment(task.uuid_id, file);
      setDetail((prev) => prev ? {
        ...prev,
        attachments: [created, ...prev.attachments],
        attachment_count: prev.attachment_count + 1,
      } : prev);
      toast.success(t('agile.attachmentUploadSuccess'));
      onRefreshBoard();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || t('agile.attachmentUploadError'));
    } finally {
      setAttachmentSaving(false);
    }
  }

  async function handleDeleteAttachment(attachment: AgileAttachment) {
    const confirmed = await confirmToast(t('agile.attachmentDeleteConfirm'), attachment.file_name);
    if (!confirmed) return;

    try {
      await deleteAgileAttachment(task.uuid_id, attachment.id);
      setDetail((prev) => prev ? {
        ...prev,
        attachments: prev.attachments.filter((item) => item.id !== attachment.id),
        attachment_count: Math.max(0, prev.attachment_count - 1),
      } : prev);
      toast.success(t('agile.attachmentDeleteSuccess'));
      onRefreshBoard();
    } catch {
      toast.error(t('agile.attachmentDeleteError'));
    }
  }

  async function handleAddRelation(relatedTask: AgileRelatedTask) {
    if (!detail) return;
    setRelationSaving(true);
    try {
      await addAgileRelation(detail.uuid_id, relatedTask.uuid_id);
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              related_tasks: [relatedTask, ...prev.related_tasks.filter((item) => item.uuid_id !== relatedTask.uuid_id)],
            }
          : prev,
      );
      setRelationQuery('');
      setRelationResults([]);
      setRelationModalOpen(false);
      toast.success(t('agile.relationAddSuccess'));
      onRefreshBoard();
    } catch {
      toast.error(t('agile.relationAddError'));
    } finally {
      setRelationSaving(false);
    }
  }

  async function handleDeleteRelation(relatedTask: AgileRelatedTask) {
    if (!detail) return;
    const confirmed = await confirmToast(t('agile.relationDeleteConfirm'), relatedTask.title);
    if (!confirmed) return;

    setRelationSaving(true);
    try {
      await deleteAgileRelation(detail.uuid_id, relatedTask.uuid_id);
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              related_tasks: prev.related_tasks.filter((item) => item.uuid_id !== relatedTask.uuid_id),
            }
          : prev,
      );
      toast.success(t('agile.relationDeleteSuccess'));
      onRefreshBoard();
    } catch {
      toast.error(t('agile.relationDeleteError'));
    } finally {
      setRelationSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
      <div className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">{t('agile.detailTitle')}</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">{detail?.title || task.title}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setActionsOpen((open) => !open);
                }}
                disabled={saving}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-violet-200 bg-white text-violet-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50 disabled:opacity-50"
                aria-label={t('agile.actions')}
              >
                <EllipsisVertical className="h-4 w-4" />
              </button>
              {actionsOpen && (
                <div
                  className="absolute right-0 top-full z-20 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setActionsOpen(false);
                      void handleAssignToMe();
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <CircleUserRound className="h-4 w-4" />
                    {t('agile.assignToMe')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActionsOpen(false);
                      onDuplicate(detail ?? task, 'duplicate');
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <FileText className="h-4 w-4" />
                    {t('agile.duplicate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActionsOpen(false);
                      onDuplicate(detail ?? task, 'related');
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <GitBranch className="h-4 w-4" />
                    {t('agile.createRelated')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActionsOpen(false);
                      void handleDelete();
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('agile.delete')}
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid min-h-0 flex-none gap-0 overflow-hidden lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-5 overflow-y-auto border-b border-slate-200 p-6 lg:h-[520px] lg:border-b-0 lg:border-r lg:max-h-[520px]">
            {loading ? (
              <div className="flex h-full min-h-[420px] items-center justify-center text-sm text-slate-500">
                {t('common.loading')}
              </div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.title')}</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.module')}</label>
                    <select
                      value={form.moduleId}
                      onChange={(e) => setForm((prev) => ({ ...prev, moduleId: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    >
                      {lookups.modules.map((module) => (
                        <option key={module.id} value={module.id}>{module.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.priority')}</label>
                    <select
                      value={form.priority}
                      onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    >
                      <option value="1">{t('agile.priority.low')}</option>
                      <option value="2">{t('agile.priority.middle')}</option>
                      <option value="3">{t('agile.priority.high')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.taskType')}</label>
                    <select
                      value={form.taskTypeId}
                      onChange={(e) => setForm((prev) => ({ ...prev, taskTypeId: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    >
                      <option value="">{t('agile.unassigned')}</option>
                      {lookups.taskTypes.map((type) => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.assignee')}</label>
                    <select
                      value={form.assignedUserId}
                      onChange={(e) => setForm((prev) => ({ ...prev, assignedUserId: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    >
                      <option value="">{t('agile.unassigned')}</option>
                      {lookups.users.filter((user) => user.is_active === 1).map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.fullname || user.username}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.dueDate')}</label>
                    <input
                      type="date"
                      value={form.dueDate}
                      onChange={(e) => setForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.description')}</label>
                    <textarea
                      rows={6}
                      value={form.description}
                      onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 lg:min-h-[170px]"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex min-h-0 flex-col overflow-hidden p-5 lg:h-[520px] lg:max-h-[520px]">
            <div className="mb-4 flex gap-2 rounded-2xl border border-slate-200 bg-slate-100 p-1">
              {([
                ['comments', t('agile.commentsShort'), MessageSquare, detail?.comments?.length ?? 0],
                ['attachments', t('agile.attachmentsShort'), Paperclip, detail?.attachments?.length ?? 0],
                ['relations', t('agile.relatedTasksShort'), GitBranch, detail?.related_tasks?.length ?? 0],
                ['history', t('agile.historyShort'), History, detail?.history?.length ?? 0],
              ] as const).map(([key, label, Icon, count]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveSideTab(key)}
                  className={`flex-1 rounded-xl px-2 py-2 text-[11px] font-semibold transition ${
                    activeSideTab === key
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{label}</span>
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] leading-none ${activeSideTab === key ? 'bg-slate-100 text-slate-700' : 'bg-white/70 text-slate-500'}`}>
                      {count}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex h-full min-h-[420px] items-center justify-center text-sm text-slate-500">
                {t('common.loading')}
              </div>
            ) : activeSideTab === 'history' ? (
              <div className="min-h-0 flex flex-1 flex-col">
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                  {(detail?.history ?? []).slice(0, 12).map((item) => (
                    <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-center justify-between gap-3 text-[11px] text-slate-500">
                        <span>{item.moved_by_name || t('agile.unknownUser')}</span>
                        <span>{item.created_on}</span>
                      </div>
                      <div className="mt-1.5 text-xs font-medium text-slate-700">
                        {item.event_type === 'status' && item.from_status && item.to_status && (
                          <>{statusTitle(item.from_status, t)} {'->'} {statusTitle(item.to_status, t)}</>
                        )}
                        {item.event_type === 'assignee' && (
                          <>
                            {item.from_assigned_user_name || t('agile.unassigned')} {'->'} {item.to_assigned_user_name || t('agile.unassigned')}
                          </>
                        )}
                        {item.event_type === 'attachment_add' && <>{item.details}</>}
                        {item.event_type === 'attachment_delete' && <>{item.details}</>}
                        {item.event_type === 'relation_add' && <>{item.details}</>}
                        {item.event_type === 'relation_delete' && <>{item.details}</>}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-400">
                        {item.event_type === 'status'
                          ? t('agile.historyStatusChange')
                          : item.event_type === 'assignee'
                            ? t('agile.historyAssigneeChange')
                            : item.event_type === 'attachment_add'
                              ? t('agile.historyAttachmentAdd')
                              : item.event_type === 'attachment_delete'
                                ? t('agile.historyAttachmentDelete')
                                : item.event_type === 'relation_add'
                                  ? t('agile.historyRelationAdd')
                                  : t('agile.historyRelationDelete')}
                      </div>
                    </div>
                  ))}
                  {!detail?.history?.length && (
                    <div className="rounded-xl border border-dashed border-slate-200 p-3 text-xs text-slate-400">
                      {t('agile.noHistory')}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {!loading && activeSideTab === 'comments' && (
              <div className="min-h-0 flex h-full flex-1 flex-col">
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                  {(detail?.comments ?? []).map((item: AgileComment) => (
                    <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-2.5">
                      <div className="flex items-center justify-between gap-3 text-[11px] text-slate-500">
                        <span className="font-medium text-slate-700">{item.user_name || t('agile.unknownUser')}</span>
                        <span>{item.created_on}</span>
                      </div>
                      <p className="mt-1.5 whitespace-pre-wrap text-xs leading-5 text-slate-700">
                        {renderCommentWithMentions(item.comment_text)}
                      </p>
                    </div>
                  ))}
                  {!detail?.comments?.length && (
                    <div className="rounded-xl border border-dashed border-slate-200 p-3 text-xs text-slate-400">
                      {t('agile.noComments')}
                    </div>
                  )}
                </div>
                <div className="mt-auto border-t border-slate-200 pt-3">
                  <div className="relative flex items-start gap-2">
                    <textarea
                      ref={commentInputRef}
                      rows={3}
                      value={comment}
                      onChange={(e) => handleCommentChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
                      onKeyDown={(e) => {
                        if (!mentionOpen || filteredMentionUsers.length === 0) return;
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setMentionSelectedIndex((prev) => (prev + 1) % filteredMentionUsers.length);
                          return;
                        }
                        if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setMentionSelectedIndex((prev) => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
                          return;
                        }
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          applyMention(filteredMentionUsers[mentionSelectedIndex]?.username ?? filteredMentionUsers[0].username);
                          return;
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setMentionOpen(false);
                          return;
                        }
                      }}
                      placeholder={t('agile.commentPlaceholder')}
                      className="min-h-[88px] flex-1 rounded-xl border border-slate-300 px-3 py-2.5 text-xs outline-none focus:border-blue-500"
                    />
                    {mentionOpen && (
                      <div className="absolute bottom-[100%] left-0 right-12 mb-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                        {filteredMentionUsers.length > 0 ? (
                          filteredMentionUsers.map((user, index) => (
                            <button
                              key={user.id}
                              type="button"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                applyMention(user.username);
                              }}
                              className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs transition ${index === mentionSelectedIndex ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                            >
                              <span className="font-medium text-slate-800">{user.fullname || user.username}</span>
                              <span className="text-slate-500">@{user.username}</span>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-xs text-slate-500">{t('agile.noMentionUsers')}</div>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleAddComment}
                      disabled={commentSaving || !comment.trim()}
                      title={t('agile.addComment')}
                      className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white transition hover:bg-slate-800 disabled:opacity-50"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!loading && activeSideTab === 'attachments' && (
              <div className="min-h-0 flex flex-1 flex-col">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-slate-800">{t('agile.attachments')}</div>
                  <label className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
                    <Upload className="h-4 w-4" />
                    {attachmentSaving ? t('common.loading') : t('agile.addAttachment')}
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleAttachmentChange}
                      className="hidden"
                      disabled={attachmentSaving}
                    />
                  </label>
                </div>

                <div className="min-h-0 flex-1 rounded-2xl border border-slate-200 bg-slate-50/70 p-2.5">
                  <div className="min-h-0 flex flex-1 flex-col gap-2 overflow-y-auto pr-1">
                    {(detail?.attachments ?? []).map((attachment) => {
                      const isImage = isImageAttachment(attachment.file_url, attachment.mime_type);
                      return (
                        <div key={attachment.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                            {isImage ? (
                              <ImageIcon className="h-4 w-4 text-blue-500" />
                            ) : (
                              <FileText className="h-4 w-4" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-semibold text-slate-700">{attachment.file_name}</div>
                            <div className="mt-0.5 truncate text-[10px] text-slate-400">
                              {isImage ? 'RESIM' : attachment.mime_type?.includes('pdf') ? 'PDF' : 'DOC'} · {formatFileSize(attachment.size_bytes)}
                            </div>
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-1.5">
                            <a
                              href={attachment.file_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex h-7 items-center justify-center rounded-lg border border-slate-200 px-2.5 text-[10px] font-medium text-slate-600 transition hover:bg-slate-50"
                            >
                              {t('agile.preview')}
                            </a>
                            <button
                              type="button"
                              onClick={() => handleDeleteAttachment(attachment)}
                              className="inline-flex h-7 items-center justify-center rounded-lg border border-rose-200 px-2.5 text-[10px] font-medium text-rose-600 transition hover:bg-rose-50"
                            >
                              {t('agile.delete')}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {!detail?.attachments?.length && (
                      <div className="rounded-xl border border-dashed border-slate-200 p-3 text-xs text-slate-400">
                        {t('agile.noAttachments')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!loading && activeSideTab === 'relations' && (
              <div className="min-h-0 flex flex-1 flex-col">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-slate-800">{t('agile.relatedTasks')}</div>
                  <button
                    type="button"
                    onClick={() => setRelationModalOpen(true)}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    <Plus className="h-4 w-4" />
                    {t('agile.addRelation')}
                  </button>
                </div>
                <div className="min-h-0 flex-1 rounded-2xl border border-slate-200 bg-slate-50/70 p-2.5">
                  <div className="min-h-0 flex flex-1 flex-col gap-2 overflow-y-auto pr-1">
                    {(detail?.related_tasks ?? []).map((item) => (
                      <div key={item.uuid_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                        <button
                          type="button"
                          onClick={() => onOpenTask(item)}
                          className="min-w-0 flex-1 text-left transition hover:opacity-80"
                        >
                          <div className="truncate text-sm font-semibold text-slate-800">{item.title}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                            <span>{item.module_name}</span>
                            {item.task_type_name && <span>· {item.task_type_name}</span>}
                            {item.assigned_user_name && <span>· {item.assigned_user_name}</span>}
                          </div>
                        </button>
                        <div className="flex flex-shrink-0 items-center gap-2">
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone(item.status)}`}>
                            {statusTitle(item.status, t)}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleDeleteRelation(item)}
                            className="inline-flex h-7 items-center justify-center rounded-lg border border-rose-200 px-2.5 text-[10px] font-medium text-rose-600 transition hover:bg-rose-50"
                          >
                            {t('agile.delete')}
                          </button>
                        </div>
                      </div>
                    ))}
                    {!detail?.related_tasks?.length && (
                      <div className="rounded-xl border border-dashed border-slate-200 p-3 text-xs text-slate-400">
                        {t('agile.noRelatedTasks')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {!loading && (
          <div className="flex-shrink-0 border-t border-slate-200 bg-slate-50 px-6 py-4">
            <div className="flex w-full items-center justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex h-10 min-w-[132px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {t('agile.save')}
              </button>
            </div>
          </div>
        )}
      </div>
      {carouselIndex !== null && (
        <ImageCarouselModal
          images={imageAttachments}
          index={carouselIndex}
          onClose={() => setCarouselIndex(null)}
          onGoTo={setCarouselIndex}
        />
      )}
      {relationModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">{t('agile.relatedTasks')}</div>
                <div className="mt-1 text-base font-semibold text-slate-900">{t('agile.addRelation')}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setRelationModalOpen(false);
                  setRelationQuery('');
                  setRelationResults([]);
                }}
                className="rounded-xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5">
              <div className="relative">
                <input
                  type="text"
                  value={relationQuery}
                  onChange={(e) => setRelationQuery(e.target.value)}
                  placeholder={t('agile.relatedTasksSearchPlaceholder')}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                  autoFocus
                />
                <div className="mt-3 max-h-80 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/70">
                  {relationQuery.trim().length < 2 ? (
                    <div className="px-3 py-3 text-xs text-slate-500">{t('agile.relatedTasksSearchHint')}</div>
                  ) : relationSearchLoading ? (
                    <div className="px-3 py-3 text-xs text-slate-500">{t('common.loading')}</div>
                  ) : relationResults.length > 0 ? (
                    relationResults.map((item) => (
                      <button
                        key={item.uuid_id}
                        type="button"
                        onClick={() => handleAddRelation(item)}
                        disabled={relationSaving}
                        className="flex w-full items-center justify-between gap-3 border-b border-slate-200 px-3 py-2 text-left transition last:border-b-0 hover:bg-white disabled:opacity-50"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-800">{item.title}</div>
                          <div className="mt-0.5 truncate text-[11px] text-slate-500">
                            {item.module_name}
                            {item.task_type_name ? ` · ${item.task_type_name}` : ''}
                            {item.assigned_user_name ? ` · ${item.assigned_user_name}` : ''}
                          </div>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone(item.status)}`}>
                          {statusTitle(item.status, t)}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-3 text-xs text-slate-500">{t('agile.relatedTasksNoResult')}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTaskModal({
  lookups,
  onClose,
  onCreated,
  initialValues,
  mode = 'default',
  sourceTaskUuidId,
  sourceTaskLabel,
}: {
  lookups: AgileLookups;
  onClose: () => void;
  onCreated: () => void;
  initialValues?: Partial<{
    title: string;
    description: string;
    moduleId: string;
    taskTypeId: string;
    priority: string;
    assignedUserId: string;
    dueDate: string;
  }>;
  mode?: 'default' | 'duplicate' | 'related';
  sourceTaskUuidId?: string;
  sourceTaskLabel?: string;
}) {
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: initialValues?.title ?? '',
    description: initialValues?.description ?? '',
    moduleId: initialValues?.moduleId ?? (lookups.modules[0] ? String(lookups.modules[0].id) : ''),
    taskTypeId: initialValues?.taskTypeId ?? '',
    priority: initialValues?.priority ?? '2',
    assignedUserId: initialValues?.assignedUserId ?? '',
    dueDate: initialValues?.dueDate ?? '',
  });

  async function handleCreate() {
    if (!form.title.trim() || !form.moduleId) {
      toast.error(t('agile.requiredError'));
      return;
    }

    const payload: AgileTaskPayload = {
      title: form.title.trim(),
      description: form.description.trim(),
      moduleId: Number(form.moduleId),
      taskTypeId: form.taskTypeId ? Number(form.taskTypeId) : null,
      priority: Number(form.priority),
      assignedUserId: form.assignedUserId ? Number(form.assignedUserId) : null,
      dueDate: form.dueDate || null,
      relatedSourceUuidId: mode === 'related' ? sourceTaskUuidId : undefined,
    };

    setSaving(true);
    try {
      await createAgileTask(payload);
      toast.success(t('agile.createSuccess'));
      onCreated();
    } catch {
      toast.error(t('agile.createError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">{t('agile.title')}</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">{t('agile.createTitle')}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {mode !== 'default' && (
          <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <div className="font-semibold">
              {mode === 'duplicate' ? t('agile.createModes.duplicate') : t('agile.createModes.related')}
            </div>
            <div className="mt-1 text-xs text-blue-700">
              {mode === 'duplicate'
                ? t('agile.createModeDuplicateHelp')
                : t('agile.createModeRelatedHelp', { task: sourceTaskLabel ?? '-' })}
            </div>
          </div>
        )}

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.title')}</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.module')}</label>
            <select
              value={form.moduleId}
              onChange={(e) => setForm((prev) => ({ ...prev, moduleId: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              {lookups.modules.map((module) => (
                <option key={module.id} value={module.id}>{module.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.priority')}</label>
            <select
              value={form.priority}
              onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="1">{t('agile.priority.low')}</option>
              <option value="2">{t('agile.priority.middle')}</option>
              <option value="3">{t('agile.priority.high')}</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.taskType')}</label>
            <select
              value={form.taskTypeId}
              onChange={(e) => setForm((prev) => ({ ...prev, taskTypeId: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="">{t('agile.selectTaskType')}</option>
              {lookups.taskTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.assignee')}</label>
            <select
              value={form.assignedUserId}
              onChange={(e) => setForm((prev) => ({ ...prev, assignedUserId: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="">{t('agile.unassigned')}</option>
              {lookups.users.filter((user) => user.is_active === 1).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.fullname || user.username}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.dueDate')}</label>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm((prev) => ({ ...prev, dueDate: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium text-slate-500">{t('agile.fields.description')}</label>
            <textarea
              rows={5}
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t border-slate-200 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            {t('agile.cancel')}
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={saving}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            {t('agile.createButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AgileBoardPage() {
  const headerActionsTarget = useHeaderActionsTarget();
  const { t } = useI18n();
  const currentUser = useAuthStore((s) => s.user);
  const [tasks, setTasks] = useState<AgileTask[]>([]);
  const [lookups, setLookups] = useState<AgileLookups | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [draggedTask, setDraggedTask] = useState<AgileTask | null>(null);
  const [touchTargetStatus, setTouchTargetStatus] = useState<AgileStatus | null>(null);
  const [selectedTask, setSelectedTask] = useState<AgileTask | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialValues, setCreateInitialValues] = useState<Partial<{
    title: string;
    description: string;
    moduleId: string;
    taskTypeId: string;
    priority: string;
    assignedUserId: string;
    dueDate: string;
  }> | null>(null);
  const [createMode, setCreateMode] = useState<'default' | 'duplicate' | 'related'>('default');
  const [createSourceTaskUuidId, setCreateSourceTaskUuidId] = useState<string | null>(null);
  const [createSourceTaskLabel, setCreateSourceTaskLabel] = useState<string | null>(null);
  const [filter, setFilter] = useState<BoardFilter>('all');
  const [moduleFilter, setModuleFilter] = useState('');
  const [taskTypeFilter, setTaskTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const touchDragRef = useRef<{ task: AgileTask; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);

  function openTaskFromReference(reference: AgileTask | AgileRelatedTask) {
    const matchedTask = tasks.find((item) => item.uuid_id === reference.uuid_id);
    if (matchedTask) {
      setSelectedTask(matchedTask);
      return;
    }

    setSelectedTask({
      id: reference.id,
      uuid_id: reference.uuid_id,
      title: reference.title,
      description: null,
      priority: reference.priority,
      status: reference.status,
      due_date: null,
      created_on: reference.created_on,
      updated_on: reference.created_on,
      module_id: 0,
      module_name: reference.module_name,
      task_type_id: null,
      task_type_code: null,
      task_type_name: reference.task_type_name,
      created_by_id: null,
      created_by_name: null,
      assigned_user_id: null,
      assigned_user_name: reference.assigned_user_name,
      comment_count: 0,
      attachment_count: 0,
    });
  }

  function loadBoard() {
    setLoading(true);
    Promise.all([fetchAgileBoard(), fetchAgileLookups()])
      .then(([board, nextLookups]) => {
        setTasks(board);
        setLookups(nextLookups);
      })
      .catch(() => setError(t('agile.loadError')))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadBoard();
  }, [t]);

  const groupedTasks = useMemo(() => {
    const searchTerms = search.trim().toLocaleUpperCase('tr-TR').split(/\s+/).filter(Boolean);
    const visibleTasks = tasks.filter((task) => {
      if (filter === 'mine' && currentUser && task.assigned_user_id !== currentUser.id) {
        return false;
      }
      if (moduleFilter && String(task.module_id) !== moduleFilter) {
        return false;
      }
      if (taskTypeFilter && String(task.task_type_id ?? '') !== taskTypeFilter) {
        return false;
      }
      if (searchTerms.length) {
        const text = [
          task.id, task.title, task.description, task.module_name, task.task_type_name,
          task.assigned_user_name, task.created_by_name, task.due_date, task.created_on,
          statusTitle(task.status, t), priorityLabel(task.priority, t),
        ].join(' ').toLocaleUpperCase('tr-TR');
        if (!searchTerms.every(term => text.includes(term))) return false;
      }
      return true;
    });

    const groups: Record<AgileStatus, AgileTask[]> = {
      todo: [],
      in_progress: [],
      test: [],
      done: [],
      closed: [],
      canceled: [],
    };

    visibleTasks.forEach((task) => {
      groups[task.status].push(task);
    });

    return groups;
  }, [currentUser, filter, moduleFilter, taskTypeFilter, search, t, tasks]);

  async function moveTask(task: AgileTask, nextStatus: AgileStatus) {
    if (task.status === nextStatus) return;
    const prevTasks = tasks;
    setTasks((current) => current.map((item) => item.uuid_id === task.uuid_id ? { ...item, status: nextStatus } : item));
    try {
      await updateAgileTask(task.uuid_id, { status: nextStatus });
      toast.success(t('agile.movedSuccess'));
      loadBoard();
    } catch {
      setTasks(prevTasks);
      toast.error(t('agile.moveError'));
    }
  }

  function statusFromPoint(clientX: number, clientY: number): AgileStatus | null {
    const target = document.elementFromPoint(clientX, clientY)?.closest('[data-agile-status]');
    const value = target?.getAttribute('data-agile-status');
    if (value && BOARD_COLUMNS.includes(value as AgileStatus)) return value as AgileStatus;
    return null;
  }

  function handleTouchDragStart(task: AgileTask) {
    touchDragRef.current = { task, moved: false };
    setTouchTargetStatus(task.status);
  }

  function handleTouchDragMove(clientX: number, clientY: number) {
    if (!touchDragRef.current) return;
    touchDragRef.current.moved = true;
    setTouchTargetStatus(statusFromPoint(clientX, clientY));
  }

  function handleTouchDragEnd(clientX: number, clientY: number) {
    const activeDrag = touchDragRef.current;
    const nextStatus = statusFromPoint(clientX, clientY) ?? touchTargetStatus;
    touchDragRef.current = null;
    setTouchTargetStatus(null);
    if (!activeDrag?.moved || !nextStatus) return;
    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 250);
    void moveTask(activeDrag.task, nextStatus);
  }

  function shouldSuppressClick() {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }

  if (error) return <LoadRetry />;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {headerActionsTarget && createPortal(<div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 text-slate-400">
            <Search size={14} />
            <input type="search" aria-label="Projelerde ara" placeholder="Projelerde ara…" value={search} onChange={(event) => setSearch(event.target.value)} className="w-32 bg-transparent text-xs text-slate-700 outline-none" />
          </label>
          <div className="flex h-8 rounded-lg border border-slate-200 bg-white p-0.5">
            <button
              type="button"
              onClick={() => setFilter('all')}
              aria-pressed={filter === 'all'}
              className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                filter === 'all' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {t('agile.filters.all')}
            </button>
            <button
              type="button"
              onClick={() => setFilter('mine')}
              aria-pressed={filter === 'mine'}
              className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                filter === 'mine' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {t('agile.filters.mine')}
            </button>
          </div>
          <select
            aria-label="Proje modülü"
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 outline-none focus:border-blue-500"
          >
            <option value="">{t('agile.filters.allModules')}</option>
            {(lookups?.modules ?? []).map((module) => (
              <option key={module.id} value={module.id}>
                {module.name}
              </option>
            ))}
          </select>
          <TaskTypeFilterDropdown
            compact
            value={taskTypeFilter}
            onChange={setTaskTypeFilter}
            options={lookups?.taskTypes ?? []}
            label={t('agile.filters.allTaskTypes')}
          />
        </div>
          <button
            type="button"
            onClick={() => {
              setCreateInitialValues(null);
              setCreateMode('default');
              setCreateSourceTaskUuidId(null);
              setCreateSourceTaskLabel(null);
              setCreateOpen(true);
            }}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg h-8 bg-blue-600 px-3 text-xs font-medium text-white transition hover:bg-blue-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Yeni
          </button>
      </div>,headerActionsTarget)}
      <section aria-label="Proje listesi" className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
      <div className="grid min-h-0 flex-1 gap-3 xl:grid-cols-6">
        {BOARD_COLUMNS.map((status) => (
          <div
            key={status}
            data-agile-status={status}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              if (draggedTask) {
                void moveTask(draggedTask, status);
                setDraggedTask(null);
              }
            }}
            className={`flex min-h-[220px] flex-col rounded-2xl border bg-slate-50/80 transition-colors ${
              touchTargetStatus === status ? 'border-blue-400 bg-blue-50/90' : 'border-slate-200'
            }`}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-3 py-3">
              <div>
                <div className="text-[13px] font-semibold text-slate-800">{statusTitle(status, t)}</div>
                <div className="mt-0.5 text-[11px] text-slate-400">{groupedTasks[status].length}</div>
              </div>
            </div>
            <div className="flex-1 space-y-2.5 overflow-y-auto p-3">
              {loading ? (
                <div className="text-sm text-slate-400">{t('common.loading')}</div>
              ) : groupedTasks[status].length > 0 ? (
                groupedTasks[status].map((task) => (
                  <TaskCard
                    key={task.uuid_id}
                    task={task}
                    onOpen={setSelectedTask}
                    onDragStart={setDraggedTask}
                    onTouchDragStart={handleTouchDragStart}
                    onTouchDragMove={handleTouchDragMove}
                    onTouchDragEnd={handleTouchDragEnd}
                    shouldSuppressClick={shouldSuppressClick}
                  />
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white/70 p-3 text-xs text-slate-400">
                  {t('agile.emptyColumn')}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      </section>

      {createOpen && lookups && (
        <CreateTaskModal
          lookups={lookups}
          initialValues={createInitialValues ?? undefined}
          mode={createMode}
          sourceTaskUuidId={createSourceTaskUuidId ?? undefined}
          sourceTaskLabel={createSourceTaskLabel ?? undefined}
          onClose={() => {
            setCreateOpen(false);
            setCreateInitialValues(null);
            setCreateMode('default');
            setCreateSourceTaskUuidId(null);
            setCreateSourceTaskLabel(null);
          }}
          onCreated={() => {
            setCreateOpen(false);
            setCreateInitialValues(null);
            setCreateMode('default');
            setCreateSourceTaskUuidId(null);
            setCreateSourceTaskLabel(null);
            loadBoard();
          }}
        />
      )}

      {selectedTask && lookups && (
        <AgileTaskModal
          key={selectedTask.uuid_id}
          task={selectedTask}
          lookups={lookups}
          onClose={() => setSelectedTask(null)}
          onRefreshBoard={loadBoard}
          onSaved={() => {
            setSelectedTask(null);
            loadBoard();
          }}
          onDuplicate={(sourceTask, mode = 'duplicate') => {
            setSelectedTask(null);
            setCreateMode(mode);
            setCreateSourceTaskUuidId(mode === 'related' ? sourceTask.uuid_id : null);
            setCreateSourceTaskLabel(`#${sourceTask.id}`);
            setCreateInitialValues({
              title: mode === 'duplicate' ? `${sourceTask.title} Kopya` : sourceTask.title,
              description: sourceTask.description ?? '',
              moduleId: String(sourceTask.module_id),
              taskTypeId: sourceTask.task_type_id ? String(sourceTask.task_type_id) : '',
              priority: String(sourceTask.priority),
              assignedUserId: '',
              dueDate: '',
            });
            setCreateOpen(true);
          }}
          onOpenTask={openTaskFromReference}
        />
      )}
    </div>
  );
}
